import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Container } from "@mui/material";

import { UserActions } from "../../pages/user_management/user_actions_enum";
import useBackend from "../../hooks/use_backend";
import { useMutation, useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageDoesNotExists from "../../pages/error_pages/not_found";
import { Card } from "../general/card";
import Snackbar from "../../scripts/snackbar";

import CustomUserForm from "./custom_user_form";
import {
  requestResetPassword,
  resetPasswordSubmit,
  validateResetPasswordTokenLink,
} from "../../scripts/api_v2/user_client";
import { NAVIGATION_PATH } from "../../enums/navigation_enum";
import { validate } from "uuid";

const UserForm = ({ actionType }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tokenValidationRequired = UserActions.RESET_PASSWORD_TEMP_LINK.action === actionType.action;
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { uuid } = useParams();
  const isValidUUID = (uuid) => {
    return validate(uuid);
  };

  const [editUserData, setEditUserData] = useState({
    name: "",
    username: "",
    company_name: "Other",
    roles: [
      {
        role_name: "organisation_installer",
        application: "Platform",
        root_access: false,
      },
    ],
    currentPassword: "",
    password: "",
    confirmPassword: "",
  });

  const backend = useBackend();
  // USE QUERIES--------------------------------------------------------------------------------------------------------------
  const { mutate: resetPasswordSubmitMutation, isSuccess: resetPasswordSubmitSuccess } = useMutation({
    mutationFn: async (user) => {
      user = {
        ...user,
        temp_link: uuid,
      };
      await resetPasswordSubmit(backend.fetch, user);
    },
    mutationKey: REACT_QUERY_KEYS.RESET_PASSWORD_SUBMIT,
    onError: (err) => {
      Snackbar.error("Could not reset password: " + err);
    },
    onSuccess: () => {
      setSuccess(true);
      Snackbar.success("Password update succeeded");
      navigate(actionType.navigate);
    },
    isLoading: (isLoading) => {
      setIsLoading(isLoading);
    },
  });

  const { mutate: resetPasswordRequestMutation, isSuccess: resetPasswordRequestSuccess } = useMutation({
    mutationFn: async (username) => await requestResetPassword(backend.fetch, username),
    mutationKey: REACT_QUERY_KEYS.RESET_PASSWORD_REQUEST_LINK,
    onError: (err) => {
      Snackbar.error("Could not send reset mail: " + err);
    },
    onSuccess: () => {
      setSuccess(true);
      Snackbar.success("Reset mail sent");
      navigate(actionType.navigate);
    },
    isLoading: (isLoading) => {
      setIsLoading(isLoading);
    },
  });

  const {
    data: tokenIsValid,
    isLoading: isLoadingTokenValid,
    error: errorTokenValid,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.VALIDATE_RESET_PASSWORD_LINK, backend.fetch],
    queryFn: () => validateResetPasswordTokenLink(backend.fetch, location.pathname.split("/")[2]),
    refetchInterval: 60000,
    enabled: !!tokenValidationRequired && isValidUUID(uuid) && uuid !== undefined,
  });

  useEffect(() => {
    // we first check if the uuid is a valid uuid in the frontend
    if (uuid !== undefined && !isValidUUID(uuid)) {
      Snackbar.error("Invalid token");
      navigate(NAVIGATION_PATH.REQUEST_PASSWORD_RESET);
    }
  }, [tokenValidationRequired, uuid]);

  useEffect(() => {
    // if the uuid is valid, we check if the token is valid in the backend as well
    if (tokenValidationRequired && isValidUUID(uuid)) {
      if (tokenIsValid?.valid !== undefined && !tokenIsValid?.valid) {
        Snackbar.error("Token is no longer valid");
        navigate(NAVIGATION_PATH.REQUEST_PASSWORD_RESET);
      }
    }
  }, [tokenIsValid]);

  // -------------------------------------------------------------------------------------------------------------------------

  const handleFieldValueChanges = async (e) => {
    const { name, value } = e.target;
    setEditUserData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const tempUrl = location.pathname.split("/")[2];
    const user = {
      ...editUserData,
      action: actionType.action,
      tempLink: tempUrl,
      NDASigned: 1,
    };

    if (actionType.action === UserActions.RESET_PASSWORD_TEMP_LINK.action) {
      resetPasswordSubmitMutation(user);
    } else if (actionType.action === UserActions.REQUEST_RESET_TEMP_LINK.action) {
      resetPasswordRequestMutation(user.username);
    }
  };

  if (isLoadingTokenValid) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <>
      {(!errorTokenValid && tokenValidationRequired) || !tokenValidationRequired ? (
        <Container
          maxWidth={"xs"}
          sx={{ height: "100vh", display: "flex", justifyContent: "center", flexDirection: "column" }}
        >
          <Card
            title={actionType.parseHeader}
            sx={{ display: "flex", alignItems: "center", flexDirection: "column", mt: -10 }}
            component="form"
          >
            <CustomUserForm
              editUserData={editUserData}
              handleFieldValueChanges={handleFieldValueChanges}
              actionType={actionType}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              signedNDA={tokenIsValid?.signed_nda}
            />
            <a
              onClick={() => navigate(NAVIGATION_PATH.LOGIN_PATH)}
              style={{ alignSelf: "flex-end", fontSize: "11px", cursor: "pointer", textDecoration: "underline" }}
            >
              Back to login
            </a>
          </Card>
        </Container>
      ) : (
        <PageDoesNotExists />
      )}
    </>
  );
};

export default UserForm;
